import type { KeyboardEvent, ReactNode } from 'react';
import React from 'react';

export interface DataTableColumn<T> {
  key: string;
  /** 컬럼 헤더. string 또는 ReactNode(정렬 토글 버튼 등 인터랙티브 헤더 포함 시 · queue #687) */
  header: ReactNode;
  render?: (row: T) => ReactNode;
  width?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  emptyMessage?: string;
  /** onRowClick: 행 클릭 시 호출. 지정하면 행에 tabIndex + keyboard 접근성 자동 적용 (R7-058) */
  onRowClick?: (row: T) => void;
  /** rowStyle: 각 행에 적용할 추가 인라인 스타일 반환 함수 (선택적) */
  rowStyle?: (row: T) => React.CSSProperties | undefined;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  emptyMessage = '데이터가 없습니다',
  onRowClick,
  rowStyle,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--spacing-8)',
          textAlign: 'center',
          color: 'var(--color-text-muted)',
          background: 'var(--color-surface-muted)',
          border: '1px dashed var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        {emptyMessage}
      </div>
    );
  }

  const handleRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(row);
    }
  };

  return (
    <table
      role="grid"
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      <thead>
        <tr style={{ background: 'var(--color-surface-muted)' }}>
          {columns.map((c) => (
            <th
              key={c.key}
              scope="col"
              style={{
                textAlign: 'left',
                padding: 'var(--spacing-3) var(--spacing-4)',
                borderBottom: '1px solid var(--color-surface-border)',
                width: c.width,
              }}
            >
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr
            key={rowKey(row)}
            aria-rowindex={rowIndex + 2}
            {...(onRowClick
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => onRowClick(row),
                  onKeyDown: (e) => handleRowKeyDown(e, row),
                  style: { cursor: 'pointer', ...(rowStyle?.(row) ?? {}) },
                }
              : { style: rowStyle?.(row) })}
          >
            {columns.map((c) => (
              <td
                key={c.key}
                style={{
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  borderBottom: '1px solid var(--color-surface-border)',
                }}
              >
                {c.render ? c.render(row) : ((row as Record<string, ReactNode>)[c.key] ?? null)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
